// CompactSearchForm.js
import React, { useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { FlightTakeoff, FlightLand, Add, Remove } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { darken, lighten } from "@mui/system";
import { supabase } from "../../utils/supabaseClient";
import TuneIcon from "@mui/icons-material/Tune";
import {
  renderCityOption,
  cityAutocompleteSlotProps,
} from "../../utils/cityAutocompleteSlots";
import AdvancedFiltersModal from "./Filters";
import Badge from "@mui/material/Badge";
import { useTheme } from "@mui/material/styles";
import { AMENITIES_MAP } from "../../utils/amenitiesMap";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Collapse, useMediaQuery } from "@mui/material";

const normalize = (str) =>
  str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() ?? "";

const CompactSearchForm = ({
  origin,
  setOrigin,
  originInput,
  setOriginInput,
  originOptions,
  destination,
  setDestination,
  destinationInput,
  setDestinationInput,
  destinationOptions,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  handleSearch,
  flightType,
  setFlightType,
  handleBackToSearch,
  searchType,
  advancedFilters,
  setAdvancedFilters,
  airbnbType,
  setAirbnbType,
  filtersOpen,
  setFiltersOpen,
  userCurrency,
  adults,
  setAdults,
  children,
  setChildren,
}) => {
  const [errors, setErrors] = useState({});
  const [isDepartureOpen, setIsDepartureOpen] = useState(false);
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [tripTypeAnchor, setTripTypeAnchor] = useState(null);
  const [adultsAnchor, setAdultsAnchor] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [collapsed, setCollapsed] = useState(false);

  React.useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  const formatRange = (dep, ret) => {
    if (!dep) return "Pick dates";
    const fmt = (d) =>
      new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return ret ? `${fmt(dep)}–${fmt(ret)}` : fmt(dep);
  };

  const summaryText =
    searchType !== "staysOnly"
      ? `${origin?.city || "Origin"} → ${destination?.city || "Destination"} · ${formatRange(departureDate, returnDate)}`
      : `${destination?.city || "Destination"} · ${formatRange(departureDate, returnDate)}`;

  const getSelectedFilterCount = () => {
    let count = 0;
    if (advancedFilters?.superhostOnly) count++;
    if (advancedFilters?.cancelPolicies?.length) count += advancedFilters.cancelPolicies.length;
    if (advancedFilters?.minPrice !== undefined) count++;
    if (advancedFilters?.maxPrice !== undefined && advancedFilters.maxPrice < 300) count++;
    Object.entries(advancedFilters || {}).forEach(([key, value]) => {
      if (key.startsWith("amenity_") && value) count++;
    });
    if (advancedFilters?.flightStops?.length) count += advancedFilters.flightStops.length;
    return count;
  };

  const validateFields = () => {
    const newErrors = {};
    if (searchType !== "staysOnly" && !origin) newErrors.origin = "Origin is required";
    if (!destination) newErrors.destination = "Destination is required";
    if (origin && destination && origin.city === destination.city) {
      newErrors.origin = "Origin and destination cannot be the same";
      newErrors.destination = "Origin and destination cannot be the same";
    }
    if (!departureDate) newErrors.departureDate = "Departure date is required";
    if (returnDate && returnDate < departureDate)
      newErrors.returnDate = "Return date must be after the departure date";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getIPAddress = async () => {
    try {
      const response = await fetch("https://api64.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Failed to fetch IP:", error);
      return "unknown";
    }
  };

  const logSearchToSupabase = async () => {
    const ipAddress = await getIPAddress();
    const { data, error } = await supabase
      .from("search_limits")
      .select("id, search_count")
      .eq("ip_address", ipAddress)
      .single();
    if (error && error.code !== "PGRST116") {
      console.error("Error checking search count:", error.message);
      return;
    }
    if (data) {
      await supabase
        .from("search_limits")
        .update({ search_count: data.search_count + 1 })
        .eq("id", data.id);
      return;
    }
    await supabase.from("search_limits").insert([{ ip_address: ipAddress, search_count: 1 }]);
  };

  const handleFormSubmit = () => {
    if (validateFields()) {
      logSearchToSupabase();
      handleSearch(searchType, advancedFilters, destination, null, null, userCurrency);
    }
    if (!isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const pillSx = {
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "0.82rem",
    letterSpacing: "0.01em",
    borderRadius: "999px",
    px: 1.5,
    py: 0,
    height: 32,
    minWidth: "auto",
    lineHeight: 1,
    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.75)",
    borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    "& .MuiButton-startIcon, & .MuiButton-endIcon": { margin: 0 },
    "& .MuiButton-startIcon": { mr: 0.5 },
    "& .MuiButton-endIcon": { ml: 0.4 },
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: "transparent",
    },
  };

  const menuSx = {
    "& .MuiPaper-root": {
      backgroundColor: theme.palette.background.paper,
      minWidth: "140px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "10px",
    },
  };

  const menuItemSx = {
    fontSize: "0.85rem",
    "&.Mui-selected": {
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary.main : lighten(theme.palette.primary.main, 0.1),
      color: "white",
      "&:hover": { backgroundColor: theme.palette.primary.main },
    },
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark" ? lighten(theme.palette.primary.main, 0.3) : lighten(theme.palette.primary.main, 0.6),
    },
  };

  const totalPax = (adults ?? 1) + (children ?? 0);

  return (
    <Box
      sx={{
        border: theme.palette.mode === "dark" ? "1px solid #444" : "1px solid #ddd",
        borderRadius: "16px",
        boxShadow: theme.palette.mode === "dark" ? "0px 3px 10px rgba(0,0,0,0.5)" : "0px 3px 10px rgba(0,0,0,0.15)",
        padding: isMobile && collapsed ? "10px 12px" : "16px 20px",
      }}
    >
      {isMobile && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, mb: collapsed ? 0 : 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800, fontSize: "1.25rem", textAlign: "center", lineHeight: 1.25,
              width: "100%", px: 1,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              "@media (max-width: 599px)": { WebkitLineClamp: 1 },
            }}
          >
            {summaryText}
          </Typography>
          <Button
            size="medium"
            onClick={() => setCollapsed((v) => !v)}
            endIcon={collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            sx={{
              textTransform: "none", fontWeight: 800, fontSize: "1rem",
              px: 1.5, py: 0.5, borderRadius: 999, alignSelf: "center",
              color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main,
              "& .MuiSvgIcon-root": { fontSize: "1rem" },
            }}
          >
            {collapsed ? "Edit Search" : "Hide"}
          </Button>
        </Box>
      )}

      {(!isMobile || !collapsed) && (
        <Collapse in={!isMobile || !collapsed} timeout={200} unmountOnExit>
          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", gap: 1, mb: 2, textAlign: "center" }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ width: isMobile ? "100%" : "auto" }}>
              {searchType === "flightsAndStays" ? "Flights & Stay Search" : "Stays Search"}
            </Typography>

            {/* Pills pushed to the right on desktop, centered on mobile */}
            {(setFlightType || setAdults) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: isMobile ? "unset" : "auto", mt: isMobile ? 1 : 0, justifyContent: "center", width: isMobile ? "100%" : "auto" }}>
                {setFlightType && searchType !== "staysOnly" && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => setTripTypeAnchor(e.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: "0.7rem !important" }} />}
                    sx={pillSx}
                  >
                    {flightType === "roundtrip" ? "Round trip" : "One way"}
                  </Button>
                )}
                {setAdults && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => setAdultsAnchor(e.currentTarget)}
                    startIcon={<PersonIcon sx={{ fontSize: "0.75rem !important" }} />}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: "0.7rem !important" }} />}
                    sx={pillSx}
                  >
                    {totalPax}
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* MAIN FORM ROW */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 0.5,
              justifyContent: "center",
              flexDirection: { xs: "column", md: "row" },
              width: "100%",
              maxWidth: "1200px",
              mx: "auto",
              mb: 1,
            }}
          >
            {/* Origin */}
            {searchType !== "staysOnly" && (
              <Box sx={{ width: { xs: "100%", md: "320px" }, maxWidth: { xs: "400px", md: "320px" }, position: "relative" }}>
                <Autocomplete
                  options={originOptions}
                  renderOption={renderCityOption}
                  slotProps={cityAutocompleteSlotProps(theme)}
                  getOptionLabel={(option) => `${option.city}, ${option.country_name}`}
                  inputValue={originInput}
                  onInputChange={(e, value) => setOriginInput(value)}
                  value={origin}
                  onChange={(e, value) => setOrigin(value)}
                  filterOptions={(options, { inputValue }) => {
                    const query = normalize(inputValue.trim());
                    return options.filter((option) => {
                      if (!option) return false;
                      return (
                        normalize(option.city).includes(query) ||
                        normalize(option.country_name).includes(query) ||
                        option.airports?.some((a) => normalize(a.iata || "").startsWith(query))
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Origin"
                      error={!!errors.origin}
                      helperText={errors.origin}
                      FormHelperTextProps={{ sx: { position: "absolute", top: "100%" } }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><FlightTakeoff /></InputAdornment>,
                      }}
                    />
                  )}
                />
              </Box>
            )}

            {/* Destination */}
            <Box
              sx={{
                width: { xs: "100%", md: searchType === "staysOnly" ? "600px" : "320px" },
                maxWidth: { xs: "400px", md: searchType === "staysOnly" ? "600px" : "320px" },
                position: "relative",
                mt: { xs: 1, md: 0 },
              }}
            >
              <Autocomplete
                options={destinationOptions}
                renderOption={renderCityOption}
                slotProps={cityAutocompleteSlotProps(theme)}
                getOptionLabel={(option) => `${option.city}, ${option.country_name}`}
                inputValue={destinationInput}
                onInputChange={(e, value) => setDestinationInput(value)}
                value={destination}
                onChange={(e, value) => setDestination(value)}
                filterOptions={(options, { inputValue }) => {
                  const query = normalize(inputValue.trim());
                  return options.filter((option) => {
                    if (!option) return false;
                    return (
                      normalize(option.city).includes(query) ||
                      normalize(option.country_name).includes(query) ||
                      option.airports?.some((a) => normalize(a.iata || "").startsWith(query))
                    );
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination"
                    error={!!errors.destination}
                    helperText={errors.destination}
                    FormHelperTextProps={{ sx: { position: "absolute", top: "100%" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <InputAdornment position="start"><FlightLand /></InputAdornment>,
                    }}
                  />
                )}
              />
            </Box>

            {/* Dates */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 0.5, alignItems: "center", width: "auto", justifyContent: "center" }}>
              <Box sx={{ width: { xs: "100%", sm: "145px", md: "140px" }, maxWidth: "400px", position: "relative", mt: { xs: 1, md: 0 } }}>
                <DatePicker
                  label="Departure Date"
                  value={departureDate}
                  onChange={(newValue) => {
                    setDepartureDate(newValue);
                    if (returnDate && newValue && returnDate < newValue) {
                      setReturnDate(null);
                      setErrors((prev) => ({ ...prev, returnDate: "Return date must be after the departure date" }));
                    } else {
                      setErrors((prev) => ({ ...prev, returnDate: undefined }));
                    }
                  }}
                  open={isDepartureOpen}
                  onOpen={() => setIsDepartureOpen(true)}
                  onClose={() => setIsDepartureOpen(false)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.departureDate,
                      helperText: errors.departureDate,
                      FormHelperTextProps: { sx: { position: "absolute", top: "100%" } },
                      inputProps: { readOnly: true, onClick: () => setIsDepartureOpen(true) },
                    },
                  }}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", sm: "145px", md: "140px" }, maxWidth: "400px", position: "relative", mt: { xs: 1, md: 0 } }}>
                <DatePicker
                  label={flightType === "oneway" ? "Checkout Date" : "Return Date"}
                  value={returnDate}
                  onChange={(newValue) => {
                    setReturnDate(newValue);
                    if (newValue && departureDate && newValue >= departureDate)
                      setErrors((prev) => ({ ...prev, returnDate: undefined }));
                  }}
                  open={isReturnOpen}
                  onOpen={() => setIsReturnOpen(true)}
                  onClose={() => setIsReturnOpen(false)}
                  minDate={departureDate ? new Date(departureDate.getTime() + 24 * 60 * 60 * 1000) : new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.returnDate,
                      helperText: errors.returnDate,
                      FormHelperTextProps: { sx: { position: "absolute", top: "100%" } },
                      inputProps: { readOnly: true, onClick: () => setIsReturnOpen(true) },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Buttons Row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { xs: 1, md: 0 }, width: "auto", justifyContent: "center", flexDirection: isMobile ? "column" : "row", ml: { xs: 0, md: 1 } }}>
              <Badge
                badgeContent={getSelectedFilterCount()}
                color="primary"
                overlap="rectangular"
                invisible={getSelectedFilterCount() === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    top: 4, right: 4, fontSize: "0.7rem", fontWeight: "bold",
                    height: 20, minWidth: 20, borderRadius: "50%",
                    backgroundColor: theme.palette.mode === "dark" ? "#fff" : "#000",
                    color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setFiltersOpen(true)}
                  startIcon={<TuneIcon sx={{ fontSize: "1.1rem !important" }} />}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    letterSpacing: "0.03em",
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    width: isMobile ? "200px" : "150px",
                    height: "56px",
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.15)",
                    backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                    backdropFilter: "blur(4px)",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                      transform: "translateY(-1px)",
                      boxShadow: theme.palette.mode === "dark" ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  Filters
                </Button>
              </Badge>

              <Button
                variant="contained"
                onClick={handleFormSubmit}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 900,
                  fontSize: "1.05rem",
                  letterSpacing: "0.05em",
                  px: { xs: 6, md: 4 },
                  py: 1.5,
                  width: isMobile ? "100%" : "150px",
                  height: "56px",
                  maxWidth: "400px",
                  background: theme.palette.mode === "dark" 
                    ? "linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)" 
                    : "linear-gradient(135deg, #5F5AA2 0%, #4A468A 100%)",
                  boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
                  color: "white",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-1px) scale(1.02)",
                    boxShadow: "0 6px 20px rgba(0,118,255,0.23)",
                    background: theme.palette.mode === "dark" 
                      ? "linear-gradient(135deg, #5d7dfa 0%, #9666d2 100%)" 
                      : "linear-gradient(135deg, #4F4A92 0%, #3A367A 100%)",
                  },
                }}
              >
                SEARCH
              </Button>
            </Box>

            <AdvancedFiltersModal
              open={filtersOpen}
              onClose={() => setFiltersOpen(false)}
              searchType={searchType}
              advancedFilters={advancedFilters}
              setAdvancedFilters={setAdvancedFilters}
              airbnbType={airbnbType}
              setAirbnbType={setAirbnbType}
              AMENITIES_MAP={AMENITIES_MAP}
            />
          </Box>
        </Collapse>
      )}

      {/* Trip Type Menu */}
      <Menu
        anchorEl={tripTypeAnchor}
        open={Boolean(tripTypeAnchor)}
        onClose={() => setTripTypeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={menuSx}
      >
        <MenuItem onClick={() => { setFlightType("roundtrip"); setTripTypeAnchor(null); }} selected={flightType === "roundtrip"} sx={menuItemSx}>
          Round Trip
        </MenuItem>
        <MenuItem onClick={() => { setFlightType("oneway"); setTripTypeAnchor(null); }} selected={flightType === "oneway"} sx={menuItemSx}>
          One Way
        </MenuItem>
      </Menu>

      {/* Passengers Menu */}
      <Menu
        anchorEl={adultsAnchor}
        open={Boolean(adultsAnchor)}
        onClose={() => setAdultsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ "& .MuiPaper-root": { backgroundColor: theme.palette.mode === "dark" ? "#2c2c2c" : "#fafafa", boxShadow: "0px 2px 10px rgba(0,0,0,0.3)", minWidth: "200px", borderRadius: "10px" } }}
      >
        <MenuItem disableRipple sx={{ display: "flex", justifyContent: "space-between", "&:hover": { backgroundColor: "transparent" }, "&.Mui-focusVisible": { backgroundColor: "transparent" } }}>
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>Adults</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => setAdults(Math.max(1, (adults ?? 1) - 1))} size="small" sx={{ color: theme.palette.mode === "dark" ? "white" : "black" }}>
              <Remove />
            </IconButton>
            <Typography>{adults ?? 1}</Typography>
            <IconButton onClick={() => setAdults(Math.min(10, (adults ?? 1) + 1))} size="small" sx={{ color: theme.palette.mode === "dark" ? "white" : "black" }}>
              <Add />
            </IconButton>
          </Box>
        </MenuItem>
        <MenuItem disableRipple sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", "&:hover": { backgroundColor: "transparent" }, "&.Mui-focusVisible": { backgroundColor: "transparent" } }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>Children</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", mt: -0.5 }}>Ages 2-12</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => setChildren(Math.max(0, (children ?? 0) - 1))} size="small" sx={{ color: theme.palette.mode === "dark" ? "white" : "black" }}>
              <Remove />
            </IconButton>
            <Typography>{children ?? 0}</Typography>
            <IconButton onClick={() => setChildren(Math.min(10, (children ?? 0) + 1))} size="small" sx={{ color: theme.palette.mode === "dark" ? "white" : "black" }}>
              <Add />
            </IconButton>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CompactSearchForm;
